// Copyright (c) 2026 Dyna Robotics, Inc. All rights reserved.
// Dyna Robotics Proprietary and Confidential.
// Unauthorized copying, use, or distribution is prohibited.
// SPDX-License-Identifier: LicenseRef-Dyna-Internal

/**
 * Copies one value — a serial, a material code — to the clipboard, next to where it is shown.
 *
 * These are read off the screen to be typed somewhere else (a supplier's RMA form, a stock
 * lookup), and a serial like `SN-J1B0O` is exactly what gets mistyped by hand. The button
 * says it worked by turning into a tick for a moment; a failure (no clipboard permission,
 * an http page) says so in the tooltip rather than pretending.
 */

import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { CheckIcon, CopyIcon } from "./icons";

const SHOWN_MS = 1500;

export function CopyButton({
  value,
  label,
  text,
}: {
  value: string;
  label: string;
  /** Shown beside the icon, for a button that is not next to the value it copies. */
  text?: string;
}) {
  const { t } = useTranslation();
  const [state, setState] = useState<"idle" | "copied" | "failed">("idle");

  useEffect(() => {
    if (state === "idle") return;
    const timer = window.setTimeout(() => setState("idle"), SHOWN_MS);
    return () => window.clearTimeout(timer);
  }, [state]);

  const title =
    state === "copied" ? t("common.copied") : state === "failed" ? t("common.copyFailed") : label;
  return (
    <button
      type="button"
      className={[
        "copy-button",
        "tooltip-trigger",
        text && "copy-button-text",
        state === "copied" && "is-copied",
      ]
        .filter(Boolean)
        .join(" ")}
      aria-label={label}
      data-tooltip={title}
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(value);
          setState("copied");
        } catch {
          setState("failed");
        }
      }}
    >
      {state === "copied" ? <CheckIcon size={14} /> : <CopyIcon size={14} />}
      {text && <span>{text}</span>}
      <span className="visually-hidden" aria-live="polite">
        {state === "idle" ? "" : title}
      </span>
    </button>
  );
}
